/**
 * La carta ampliada, con su efecto en un tamaño que se puede leer.
 *
 * Existe porque el resto de la interfaz no puede: en el tablero caben 36 cartas
 * y en la mano hasta siete, así que ahí el texto del efecto o va a 7 píxeles o
 * no cabe. Ninguna de las dos opciones sirve para entender qué hace una carta
 * antes de jugarla, que es la decisión central del juego.
 *
 * Se abre manteniendo pulsada cualquier carta, esté donde esté.
 */

import { motion } from 'framer-motion';
import { Coins, Layers, Sparkles, Swords, X } from 'lucide-react';
import { getCardMaster } from '@/data';
import { basePowerOf } from '@/engine/effects';
import { RARITY_STYLES, artUrlFor, iconFor } from './cardArt';
import type { CardInstance } from '@/types/game';

interface CardDetailProps {
  card: CardInstance;
  /** Poder actual si está en el tablero; sin él se muestra el impreso. */
  currentPower?: number;
  /** Número de serie, cuando se abre desde la colección o el mercado. */
  serialNumber?: number | null;
  maxSupply?: number | null;
  onClose: () => void;
}

export function CardDetail({
  card,
  currentPower,
  serialNumber,
  maxSupply,
  onClose,
}: CardDetailProps) {
  const master = getCardMaster(card.masterId);
  const rarity = RARITY_STYLES[master.rarity];
  const Icon = iconFor(master.art.icon);
  const artUrl = artUrlFor(master);
  const base = basePowerOf(card);
  const power = currentPower ?? base;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-label={`Detalle de ${master.name}`}
    >
      <motion.div
        initial={{ scale: 0.85, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        onClick={(event) => event.stopPropagation()}
        /*
         * Acotado al alto de la pantalla. Una carta 5:7 a 320 px de ancho mide
         * 448 px solo de ilustración, y con el texto debajo pasa de 650: en un
         * móvil de 640 px de alto, el efecto —lo único que esta pantalla existe
         * para enseñar— quedaba fuera y sin forma de llegar a él.
         */
        className={`flex max-h-[88dvh] w-full max-w-xs flex-col overflow-y-auto overscroll-contain rounded-2xl border-2 bg-slate-900 shadow-2xl ${rarity.border}`}
      >
        {/* Ilustración a tamaño grande, con la proporción de la carta. */}
        <div
          className={`relative aspect-5/7 max-h-[45dvh] w-full shrink-0 bg-linear-to-br ${master.art.gradient}`}
        >
          {artUrl ? (
            <img src={artUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <Icon className="h-1/2 w-1/2 text-white/85" strokeWidth={1.5} aria-hidden />
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="absolute top-2 right-2 grid h-8 w-8 place-items-center rounded-full bg-slate-950/70 text-slate-200 backdrop-blur-xs"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>

          <span className={`absolute top-2 left-2 rounded-full bg-slate-950/70 px-2 py-0.5 text-[11px] font-bold backdrop-blur-xs ${rarity.text}`}>
            {rarity.label}
          </span>
        </div>

        <div className="shrink-0 space-y-3 p-4">
          <div>
            <h3 className="text-lg leading-tight font-black text-white">{master.name}</h3>
            {serialNumber != null ? (
              <p className="font-mono text-xs text-amber-200">
                #{serialNumber}
                {maxSupply != null ? ` de ${maxSupply}` : ''}
              </p>
            ) : null}
          </div>

          {/* Coste y poder grandes: son los dos números con los que se decide. */}
          <div className="flex gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-lg bg-sky-950/60 px-3 py-2 ring-1 ring-sky-400/30">
              <Coins className="h-4 w-4 text-sky-300" aria-hidden />
              <div className="leading-none">
                <p className="text-[10px] text-sky-300/80">Coste</p>
                <p className="text-lg font-bold tabular-nums text-white">{master.cost}</p>
              </div>
            </div>
            <div className="flex flex-1 items-center gap-2 rounded-lg bg-amber-950/60 px-3 py-2 ring-1 ring-amber-400/30">
              <Swords className="h-4 w-4 text-amber-300" aria-hidden />
              <div className="leading-none">
                <p className="text-[10px] text-amber-300/80">Poder</p>
                <p className="text-lg font-bold tabular-nums text-white">
                  {power}
                  {/* Si un efecto la ha modificado, se enseña de dónde viene. */}
                  {power !== base ? (
                    <span
                      className={`ml-1 text-xs ${power > base ? 'text-emerald-300' : 'text-rose-300'}`}
                    >
                      ({base} {power > base ? '+' : '−'} {Math.abs(power - base)})
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
          </div>

          {/* El motivo de existir de esta pantalla. */}
          {master.effectDescription ? (
            <div className="rounded-lg bg-slate-800/70 p-3">
              <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold tracking-wide text-slate-400 uppercase">
                <Sparkles className="h-3 w-3" aria-hidden />
                {master.effectType === 'PASSIVE' ? 'Efecto continuo' : 'Al revelarse'}
              </p>
              <p className="text-sm leading-snug text-slate-100">{master.effectDescription}</p>
            </div>
          ) : (
            <p className="rounded-lg bg-slate-800/40 p-3 text-sm text-slate-400">
              Sin efecto especial. Solo aporta su poder.
            </p>
          )}

          {card.level > 1 ? (
            <p className="flex items-center gap-1.5 text-xs text-amber-200">
              <Layers className="h-3 w-3" aria-hidden />
              Nivel {card.level} · +{card.level - 1} de poder por fusión
            </p>
          ) : null}
        </div>
      </motion.div>
    </motion.div>
  );
}
